import { demoActive } from '@cusp/api-client/demo';
/**
 * The learner app's ONE brand syncer.
 *
 * `useBrandSync` asks for exactly one instance per app (its doc comment says
 * why: two live syncers for two different tenants can only race, and the loser's
 * colour stays up until something re-renders). This app has three top-level
 * layouts and no single authenticated shell to hang it on, so the syncer lives
 * in the root route component and decides for itself, from the router's
 * location, whether the surface under it is one a school may paint.
 *
 * WHERE THE BRAND IS NOT ALLOWED, and it is not a detail. `/` is the public
 * front door and `/login` is the door itself: neither knows a tenant, both are
 * addressed to people who may belong to no school at all, and a signed-in
 * visitor arriving at the marketing page in their university's orange would be
 * reading a Cusp Media page dressed as their school's.
 *
 * SO THE PAINT IS SUPPRESSED, NOT THE TENANT. The hook is handed the acting
 * tenant on every route and `paint: false` on those two paths. Withholding the
 * tenant instead — which is what this component used to do — reads to the hook
 * as "nobody is signed in", and that clears the school's default DIRECTION
 * along with its colour. A reader at a Canvas school then gets the front door
 * painted Canvas by the pre-paint script and `initTheme()`, and flipped to
 * Obsidian the instant React commits: an unbranded page, correctly, in a colour
 * scheme nobody chose. Everything else in this bundle is a signed-in surface
 * and wears the brand.
 *
 * The component renders nothing. It is mounted, never composed.
 */
import { useRouterState } from '@tanstack/react-router';
import { useIdentity, useTenantBranding } from '@cusp/api-client';
import { useBrandSync, type BrandTenant } from '@cusp/theme';

/**
 * Is this path one a school is allowed to paint?
 *
 * Exported for the suite, and matched on SEGMENTS rather than with a bare
 * `startsWith`: `'/loginish'` starts with `/login` and is not the sign-in
 * screen. Kept in lockstep with the same test in each app's pre-paint script
 * (`apps/web/index.html`) — that script makes this decision before React
 * exists, and the two disagreeing is a brand that paints and is then torn down
 * (or the gold flash the cache exists to prevent).
 */
export function brandablePath(pathname: string): boolean {
  if (pathname === '/') return false;
  return !(pathname === '/login' || pathname.startsWith('/login/'));
}

export function BrandSync() {
  const identity = useIdentity();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const branding = useTenantBranding();

  const tenant: BrandTenant | null =
    identity === null ? null : { id: identity.tenantId, slug: identity.tenantSlug };

  // `status` and `data` only — the hook takes a query's shape, not a query, so
  // that `@cusp/theme` never learns how to make an HTTP request.
  useBrandSync({ status: branding.status, data: branding.data }, tenant, {
    paint: brandablePath(pathname),
    persist: !demoActive(),
  });
  return null;
}
